/**
 * AuthContext Api - Integra la nueva API backend
 * Mantiene compatibilidad con el AuthContext original pero usa la nueva API
 */

import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  getOrCreateUserProfile, 
  getClienteData
} from '../services/authApiService';
import toastService from '@/utils/toastService';
import { normalizeDataObject } from '@/utils/normalizationUtils';

// Crear el contexto
const AuthContextApi = createContext();

// Provider para el contexto de autenticación Api
export const AuthProviderApi = ({ children }) => {
  const [supabaseUser, setSupabaseUser] = useState(null);
  const [clienteData, setClienteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const processingRef = useRef(false);
  const processedUserRef = useRef(null);
  
  // Usar hooks de Clerk
  const { isLoaded, userId, sessionId, signOut, getToken } = useClerkAuth();
  const { user: clerkUser } = useUser();
  
  // Logging para detectar cambios en los datos de Clerk
  const previousUserIdRef = useRef();
  const previousSessionIdRef = useRef();
  const previousClerkUserIdRef = useRef();
  
  if (previousUserIdRef.current !== userId) {
    //console.log('🔄 [AuthApi] userId changed:', previousUserIdRef.current, '→', userId);
    previousUserIdRef.current = userId;
  }
  
  if (previousSessionIdRef.current !== sessionId) {
    //console.log('🔄 [AuthApi] sessionId changed:', previousSessionIdRef.current, '→', sessionId);
    previousSessionIdRef.current = sessionId;
  }
  
  if (previousClerkUserIdRef.current !== clerkUser?.id) {
    //console.log('🔄 [AuthApi] clerkUser.id changed:', previousClerkUserIdRef.current, '→', clerkUser?.id);
    previousClerkUserIdRef.current = clerkUser?.id;
  }
  const navigate = useNavigate();
  const location = useLocation();
  
  // Verificar si estamos en onboarding para evitar llamadas a API innecesarias
  const enOnboarding = location.pathname.includes('/onboarding');

  // Verificar si hay sesión al cargar la aplicación
  useEffect(() => {
    const verificarAuth = async () => {
      if (!isLoaded) return; // Esperar a que Clerk cargue
      
      // No hacer nada durante onboarding
      if (enOnboarding) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        if (userId && clerkUser) {
          console.log("🔐 [AuthApi] Usuario autenticado con Clerk:", userId);
          console.log("🔐 [AuthApi] Usuario Clerk completo:", {
            id: clerkUser.id,
            emailAddresses: clerkUser.emailAddresses,
            firstName: clerkUser.firstName,
            lastName: clerkUser.lastName
          });
          
          // Evitar procesamiento duplicado usando SOLO el userId estable
          if (processingRef.current || processedUserRef.current === userId) {
            console.log("🔄 [AuthApi] Ya procesando usuario, saltando...");
            setLoading(false);
            return;
          }
          
          processingRef.current = true;
          
          try {
            // Usar la nueva API para obtener/crear el usuario
            console.log("🌐 [AuthApi] Obteniendo usuario con nueva API...");
            const user = await getOrCreateUserProfile();
            
            if (user) {
              console.log("✅ [AuthApi] Usuario obtenido:", user);
              const normalizedUser = normalizeDataObject(user, 'usuario');
              setSupabaseUser(normalizedUser);
              
              // Si es cliente, cargar datos adicionales del cliente usando userId estable
              if (normalizedUser.TipoUsuario === 'Cliente' || normalizedUser.tipo_usuario === 'Cliente') {
                console.log("👤 [AuthApi] Cargando datos de cliente...");
                await loadClienteDataApi(userId); // Usar userId en lugar de clerkUser.id
              }
              
              // Verificar si es super admin basado en el tipo de usuario
              const tipoUsuario = normalizedUser.TipoUsuario || normalizedUser.tipo_usuario || normalizedUser.tipoUsuario;
              if (tipoUsuario === 'SuperAdmin') {
                console.log("🔒 [AuthApi] Usuario detectado como Super Admin");
                setIsSuperAdmin(true);
              } else if (tipoUsuario === 'Admin') {
                console.log("🔒 [AuthApi] Usuario detectado como Admin regular");
                setIsSuperAdmin(false);
              } else {
                setIsSuperAdmin(false);
              }
              
              processedUserRef.current = userId; // Usar userId estable
            } else {
              console.error("❌ [AuthApi] No se pudo obtener el usuario");
              setSupabaseUser(null);
            }
          } catch (error) {
            console.error("❌ [AuthApi] Error procesando usuario:", error);
            
            // Si es error de autenticación, limpiar estado
            if (error.response?.status === 401) {
              setSupabaseUser(null);
              setClienteData(null);
              setIsSuperAdmin(false);
            } else {
              // Para otros errores, mostrar mensaje pero no limpiar estado
              toastService.error('Error al cargar datos del usuario');
            }
          } finally {
            processingRef.current = false;
          }
        } else {
          console.log("🚫 [AuthApi] No hay usuario autenticado");
          setSupabaseUser(null);
          setClienteData(null);
          setIsSuperAdmin(false);
          
          // Limpiar refs cuando no hay usuario
          processingRef.current = false;
          processedUserRef.current = null;
        }
      } catch (error) {
        console.error('❌ [AuthApi] Error verificando sesión:', error);
        setSupabaseUser(null);
        setClienteData(null);
        setIsSuperAdmin(false);
      } finally {
        setLoading(false);
        setInitialCheckDone(true);
      }
    };
  
    if (isLoaded) {
      ////console.log("🔄 [AuthApi] Clerk cargado, verificando auth:", { userId, sessionId, isLoaded });
      verificarAuth();
    } else {
      ////console.log("⏳ [AuthApi] Esperando a que Clerk se cargue...");
    }
  }, [isLoaded, userId, navigate, enOnboarding]); // Solo dependencias estables

  // Función para cargar datos del cliente usando la nueva API
  const loadClienteDataApi = async (clerkId) => {
    try {
      ////console.log("👤 [AuthApi] Cargando datos del cliente...");
      
      const cliente = await getClienteData(clerkId);
      
      if (cliente) {
        ////console.log("✅ [AuthApi] Datos del cliente cargados:", cliente);
        const normalizedCliente = normalizeDataObject(cliente, 'cliente');
        setClienteData(normalizedCliente);
      } else {
        //console.log("ℹ️ [AuthApi] No se encontraron datos del cliente");
        setClienteData(null);
      }
    } catch (error) {
      console.error('❌ [AuthApi] Error al cargar datos del cliente:', error);
      setClienteData(null);
    }
  };

  // Función para cerrar sesión - memoizada
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      
      //console.log("🚪 [AuthApi] Cerrando sesión...");
      
      // Cerrar sesión en Clerk
      if (signOut) {
        await signOut();
      }
      
      // Limpiar estado local
      setSupabaseUser(null);
      setClienteData(null);
      setIsSuperAdmin(false);
      
      // Limpiar refs
      processingRef.current = false;
      processedUserRef.current = null;
      
      //console.log("✅ [AuthApi] Sesión cerrada correctamente");
      toastService.info('Sesión cerrada correctamente');
      return true;
    } catch (error) {
      console.error('❌ [AuthApi] Error al cerrar sesión:', error);
      toastService.error('Error al cerrar sesión');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [signOut]);

  // Función de compatibilidad para recargar datos del cliente - memoizada
  const loadClienteData = useCallback(async (usuario) => {
    if (!usuario || (usuario.TipoUsuario !== 'Cliente' && usuario.tipo_usuario !== 'Cliente')) {
      return;
    }

    const clerkId = usuario.ClerkID || usuario.clerk_id || userId; // Usar userId estable
    if (clerkId) {
      await loadClienteDataApi(clerkId);
    }
  }, [userId]);

  // Función para refrescar datos del usuario - memoizada
  const refreshUserData = useCallback(async () => {
    if (!userId) return; // Usar userId estable
    
    try {
      setLoading(true);
      ////console.log("🔄 [AuthApi] Refrescando datos del usuario...");
      
      const user = await getOrCreateUserProfile();
      if (user) {
        setSupabaseUser(user);
        
        if (user.TipoUsuario === 'Cliente' || user.tipo_usuario === 'Cliente') {
          await loadClienteDataApi(userId); // Usar userId estable
        }
        
        const tipoUsuario = user.TipoUsuario || user.tipo_usuario || user.tipoUsuario;
        if (tipoUsuario === 'SuperAdmin') {
          setIsSuperAdmin(true);
        } else if (tipoUsuario === 'Admin') {
          setIsSuperAdmin(false);
        } else {
          setIsSuperAdmin(false);
        }
      }
    } catch (error) {
      console.error('❌ [AuthApi] Error al refrescar datos del usuario:', error);
      toastService.error('Error al refrescar datos del usuario');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Pre-computar roles para evitar recálculos constantes
  const isAdmin = useMemo(() => {
    return supabaseUser ? (supabaseUser.TipoUsuario === 'Admin' || supabaseUser.tipo_usuario === 'Admin') : false;
  }, [supabaseUser?.TipoUsuario, supabaseUser?.tipo_usuario]);
  
  const isCliente = useMemo(() => {
    return supabaseUser ? (supabaseUser.TipoUsuario === 'Cliente' || supabaseUser.tipo_usuario === 'Cliente') : false;
  }, [supabaseUser?.TipoUsuario, supabaseUser?.tipo_usuario]);

  // Valores que se proporcionarán a través del contexto - memoizado
  const value = useMemo(() => {
    //console.log('🔧 [AuthApi] Context value recreado - timestamp:', new Date().toLocaleTimeString());
    
    return {
      // Datos principales
      user: supabaseUser,
      clerkUser,
      clienteData,
      
      // Estados
      loading,
      initialCheckDone,
      
      // Permisos y roles pre-computados
      isAdmin,
      isCliente,
      isSuperAdmin,
      
      // Funciones memoizadas
      logout,
      loadClienteData,
      refreshUserData,
      
      // Información adicional para debugging
      authVersion: 'api',
      usingNewApi: true
    };
  }, [
    supabaseUser,
    clerkUser,
    clienteData,
    loading,
    initialCheckDone,
    isAdmin,
    isCliente,
    isSuperAdmin,
    logout,
    loadClienteData,
    refreshUserData
  ]);

  return (
    <AuthContextApi.Provider value={value}>
      {children}
    </AuthContextApi.Provider>
  );
};

// Hook personalizado para usar el contexto de autenticación Api
export const useAuthApi = () => {
  const context = useContext(AuthContextApi);
  if (!context) {
    throw new Error('useAuthApi debe ser usado dentro de un AuthProviderApi');
  }
  return context;
};

// Hook de compatibilidad que puede usar tanto V1 como Api
export const useAuth = () => {
  //console.log('🔑 useAuth called - timestamp:', new Date().toLocaleTimeString());
  
  // Intentar usar Api primero
  const apiContext = useContext(AuthContextApi);
  
  if (apiContext) {
    return apiContext;
  }
  
  // Si no hay Api, intentar usar V1
  throw new Error('useAuth debe ser usado dentro de un AuthProviderApi');
};